import { useState, type FormEvent } from "react";
import { Link, useLocation, useSearch } from "wouter";
import { Button } from "../components/Button";
import { Card } from "../components/Card";
import { Field } from "../components/Field";
import { Footer } from "../components/Footer";
import { ApiError, apiSend } from "../lib/customFetch";
import { advanceAuthenticationGeneration } from "../lib/session-events";
import styles from "./AuthLayout.module.css";

interface LoginResponse {
  redirectTo: string;
}

export function LoginPage() {
  const [, navigate] = useLocation();
  const search = useSearch();
  const accountDeactivated =
    new URLSearchParams(search).get("konto") === "deaktiveret";
  const sessionExpired =
    new URLSearchParams(search).get("session") === "udloebet";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    apiSend<LoginResponse>("POST", "/api/login", { email, password })
      .then((res) => {
        advanceAuthenticationGeneration();
        navigate(res.redirectTo);
      })
      .catch((err: unknown) => {
        setBusy(false);
        if (err instanceof ApiError && err.status === 429) {
          setError("For mange forsøg — vent lidt og prøv igen.");
        } else if (err instanceof ApiError && err.status === 401) {
          setError("Forkert email eller adgangskode.");
        } else {
          setError("Login kunne ikke gennemføres — prøv igen.");
        }
      });
  };

  return (
    <div className={styles.page}>
      <div className={styles.box}>
        <div className={styles.brand}>
          app<span>smeden</span>
        </div>
        <Card title="Log ind">
          {accountDeactivated && (
            <p className={styles.notice} role="status">
              Din konto er deaktiveret, og alle enheder er logget ud. Du kan
              aktivere kontoen igen ved at{" "}
              <Link href="/glemt-password">oprette en ny adgangskode</Link>.
            </p>
          )}
          {sessionExpired && !accountDeactivated && (
            <p className={styles.notice} role="status">
              Din session er udløbet. Log ind igen for at fortsætte.
            </p>
          )}
          <form className={styles.form} onSubmit={submit}>
            <Field
              label="Email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <Field
              label="Adgangskode"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            {error !== null && (
              <p className={styles.error} role="alert">
                {error}
              </p>
            )}
            <Button type="submit" disabled={busy}>
              {busy ? "Logger ind…" : "Log ind"}
            </Button>
          </form>
        </Card>
        <p className={styles.footer}>
          <Link href="/glemt-password">Glemt adgangskode?</Link>
        </p>
        <p className={styles.footer}>
          Ny virksomhed? <Link href="/signup">Opret dig gratis</Link>
        </p>
        <Footer />
      </div>
    </div>
  );
}
